const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface TimelineEvent {
  type: string;
  action: string;
  timestamp: string;
  data?: any;
}

export interface TimelineEventResponse {
  success: boolean;
  call_metadata: {
    call_id: string;
    status: string;
    start_time?: string;
    end_time?: string;
    call_duration?: number;
    career_url?: string;
  };
  timeline_events: TimelineEvent[];
  violation_summary: {
    total_violations: number;
    violation_types: Record<string, number>;
    timeline_stats: {
      total_events: number;
      violation_events: number;
      first_event_time?: string;
      last_event_time?: string;
    };
  };
  filter_applied: string;
}

export class TimelineEventService {
  // Global event suppression for critical violations that end the session
  private static suppressionEndTime = 0;
  private static readonly SUPPRESSION_DURATION_MS = 2000; // 2 seconds suppression after critical violations
  private static readonly BEFOREUNLOAD_SUPPRESSION_MS = 8000; // 8 seconds suppression after beforeunload

  // The interview link's token is the candidate's credential for the timeline endpoints. Kept for
  // the tab so the completion page, after the call, can still prove which call it asks about.
  static setInterviewLink(link: string): void {
    try {
      sessionStorage.setItem('voxi-interview-link', link);
    } catch {
      // Storage blocked: requests go without it
    }
  }

  // A call started without an interview link must not send a link left by an earlier interview
  // in the same tab: the API refuses a link that belongs to another call.
  static clearInterviewLink(): void {
    try {
      sessionStorage.removeItem('voxi-interview-link');
    } catch {
      // Storage blocked: nothing was stored
    }
  }

  private static getInterviewLink(): string | null {
    try {
      return sessionStorage.getItem('voxi-interview-link');
    } catch {
      return null;
    }
  }

  private static getCallServiceUrl(): string {
    return import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
  }

  /**
   * Trigger global event suppression after critical violations
   */
  private static triggerEventSuppression(reason: string, duration?: number): void {
    const suppressionDuration = duration || this.SUPPRESSION_DURATION_MS;
    this.suppressionEndTime = Date.now() + suppressionDuration;
    console.log(`[TimelineEventService] Event suppression activated for ${suppressionDuration}ms due to: ${reason}`);
  }

  /**
   * Check if events are currently suppressed
   */
  private static isEventsSuppressed(): boolean {
    const now = Date.now();
    const suppressed = now < this.suppressionEndTime;
    if (suppressed) {
      const remainingMs = this.suppressionEndTime - now;
      console.log(`[TimelineEventService] Events suppressed - ${remainingMs}ms remaining`);
    }
    return suppressed;
  }

  /**
   * Reset event suppression (for new calls)
   */
  static resetEventSuppression(): void {
    this.suppressionEndTime = 0;
  }

  /**
   * Check if event is monitor detection type
   */
  private static isMonitorDetectionEvent(eventData: TimelineEvent): boolean {
    return eventData.type === 'monitor_detection';
  }

  /**
   * Add a timeline event to a call's timeline_events array
   */
  static async addTimelineEvent(callId: string, eventData: TimelineEvent): Promise<void> {
    // Until the call is created the page passes a stand-in id (the interview link, the
    // session id or "session-<time>"); the API stores events only on a real call id.
    if (!UUID_RE.test(callId)) {
      return;
    }

    // Check if events are currently suppressed due to critical violations
    // Allow monitor detection events to bypass suppression
    if (this.isEventsSuppressed() && !this.isMonitorDetectionEvent(eventData)) {
      return;
    }

    // Log when monitor detection bypasses suppression
    if (this.isEventsSuppressed() && this.isMonitorDetectionEvent(eventData)) {
    }

    // Trigger suppression for critical violations that end the session
    const isCriticalViolation = (
      eventData.type === 'screen_share_violation' ||
      (eventData.type === 'monitor_detection' && eventData.action === 'violation')
    );
    
    // Special handling for beforeunload - longer suppression period
    const isBeforeUnload = eventData.type === 'unload' && eventData.action === 'beforeunload_attempt';
    
    if (isBeforeUnload) {
      this.triggerEventSuppression('beforeunload detected', this.BEFOREUNLOAD_SUPPRESSION_MS);
    } else if (isCriticalViolation) {
      this.triggerEventSuppression(`${eventData.type} violation detected`);
    }

    const callServiceUrl = this.getCallServiceUrl();
    

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      const link = this.getInterviewLink();
      if (link) headers["x-interview-link"] = link;

      const requestBody = {
        events: [eventData]
      };
      
      const url = `${callServiceUrl}/api/call/${callId}/timeline-events`;
      

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });


      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Timeline event API failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[TimelineEventService] Timeline event stored successfully');
    } catch (error) {
      console.error('[TimelineEventService] Failed to store timeline event');
      throw error;
    }
  }

  /**
   * Get timeline events for a call
   */
  static async getTimelineEvents(
    callId: string,
    eventTypes: 'violations' | 'all' = 'all'
  ): Promise<TimelineEventResponse> {
    const callServiceUrl = this.getCallServiceUrl();
    
    console.log('🔍 [TimelineEventService] STARTING TIMELINE EVENTS FETCH:');
    console.log('  - callId parameter:', callId);
    console.log('  - callId type:', typeof callId);
    console.log('  - callId length:', callId?.length);
    console.log('  - eventTypes:', eventTypes);
    console.log('  - callServiceUrl:', callServiceUrl);

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      const link = this.getInterviewLink();
      if (link) headers["x-interview-link"] = link;

      // Build URL with query parameters
      const url = new URL(`${callServiceUrl}/api/call/${callId}/timeline-events`);
      if (eventTypes !== 'all') {
        url.searchParams.append('event_types', eventTypes);
      }
      
      console.log('🔍 [TimelineEventService] MAKING GET REQUEST:');
      console.log('  - Final URL:', url.toString());
      console.log('  - callId in URL:', callId);
      console.log('  - Full endpoint:', `${callServiceUrl}/api/call/${callId}/timeline-events`);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers,
      });

      console.log('🔍 [TimelineEventService] RESPONSE RECEIVED:');
      console.log('  - Status:', response.status);
      console.log('  - Status text:', response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 [TimelineEventService] API ERROR RESPONSE:');
        console.error('  - Status:', response.status);
        console.error('  - Error text:', errorText);
        throw new Error(`Timeline events API failed: ${response.status} - ${errorText}`);
      }

      const result: TimelineEventResponse = await response.json();
      console.log('[TimelineEventService] Timeline events fetched successfully:', {
        totalEvents: result.timeline_events.length,
        totalViolations: result.violation_summary.total_violations,
        filterApplied: result.filter_applied
      });
      
      return result;
    } catch (error) {
      console.error('[TimelineEventService] Failed to fetch timeline events:', error);
      throw error;
    }
  }

  /**
   * Create a monitor detection timeline event
   */
  static createMonitorDetectionEvent(
    hasMultipleMonitors: boolean,
    screenCount: number,
    previousScreenCount: number,
    detectionMethod: string,
    confidence: number | string,
    isNewViolation: boolean,
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'monitor_detection',
      action: hasMultipleMonitors ? 'violation' : 'passed',
      timestamp: new Date().toISOString(),
      data: {
        hasMultipleMonitors,
        screenCount,
        previousScreenCount,
        detectionMethod,
        confidence,
        isNewViolation,
        ...additionalData
      }
    };
  }

  /**
   * Create a tab change timeline event
   */
  static createTabChangeEvent(
    action: 'hidden' | 'visible',
    duration?: number,
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'tab_change',
      action,
      timestamp: new Date().toISOString(),
      data: {
        duration,
        windowTitle: document.title,
        url: window.location.href,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...additionalData
      }
    };
  }

  /**
   * Create a window blur/focus timeline event
   */
  static createWindowBlurEvent(
    action: 'blur' | 'focus',
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'window_blur',
      action,
      timestamp: new Date().toISOString(),
      data: {
        documentHidden: document.hidden,
        visibilityState: document.visibilityState,
        ...additionalData
      }
    };
  }

  /**
   * Create a window manipulation timeline event
   */
  static createWindowManipulationEvent(
    action: 'resize' | 'maximize' | 'restore' | 'move',
    previousState: any,
    currentState: any,
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'window_manipulation',
      action,
      timestamp: new Date().toISOString(),
      data: {
        previousState,
        currentState,
        documentHidden: document.hidden,
        visibilityState: document.visibilityState,
        ...additionalData
      }
    };
  }

  /**
   * Create a beforeunload/unload timeline event
   */
  static createUnloadEvent(
    action: 'beforeunload_attempt' | 'unload_confirmed',
    callActive: boolean,
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'unload',
      action,
      timestamp: new Date().toISOString(),
      data: {
        userAgent: navigator.userAgent,
        callActive,
        ...additionalData
      }
    };
  }

  /**
   * Create a fullscreen violation timeline event
   */
  static createFullscreenViolationEvent(
    action: 'fullscreen_exit' | 'fullscreen_escape_key',
    message: string,
    severity: 'warning' | 'penalty' | 'critical',
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'fullscreen_violation',
      action,
      timestamp: new Date().toISOString(),
      data: {
        message,
        severity,
        violationType: action,
        documentHidden: document.hidden,
        visibilityState: document.visibilityState,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...additionalData
      }
    };
  }

  /**
   * Create a screen sharing violation timeline event
   */
  static createScreenShareViolationEvent(
    action: 'screen_share_stopped' | 'screen_share_ended' | 'permission_denied',
    reason: 'user_stopped' | 'track_ended' | 'permission_denied',
    severity: 'warning' | 'penalty' | 'critical',
    additionalData?: any
  ): TimelineEvent {
    return {
      type: 'screen_share_violation',
      action: 'violation',
      timestamp: new Date().toISOString(),
      data: {
        violationType: action,
        terminationReason: reason,
        severity,
        message: `Screen sharing ${action.replace('_', ' ')} - ${reason.replace('_', ' ')}`,
        documentHidden: document.hidden,
        visibilityState: document.visibilityState,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...additionalData
      }
    };
  }
} 
